import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, CheckCircle, CreditCard, Wallet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useCart } from "@/contexts/CartContext";
import { toast } from "sonner";
import { initiatePayment } from "@/services/payment.service";
import { placeOrder } from "@/services/order.service";

const PaymentOptions = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { cart, cartData, clearCart } = useCart();
  const [showSuccess, setShowSuccess] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<
    "online" | "cod" | null
  >("online");
  const [isProcessing, setIsProcessing] = useState(false);

  // Get delivery and billing address IDs from navigation state
  const deliveryAddressId = location.state?.deliveryAddressId;
  const billingAddressId = location.state?.billingAddressId;

  // Helper function to get final price


  const getFinalPrice = (product: any) => {
    console.log(
      "Calculating final price for product:",
      product.total - product.base_total,
      product.base_total,
      product
    );
    return (
      parseFloat(product.base_total) +
      (parseFloat(product.total) - parseFloat(product.base_total))
    );
  };
    const subtotal = cart.reduce(
    (sum, item) => sum + getFinalPrice(item.product) * item.quantity,
    0
  );
  const deliveryFee = 0;
  const total:any = subtotal + deliveryFee;
  // Load Razorpay script
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const paymentMethods = [
    {
      id: "online",
      icon: CreditCard,
      title: "Pay Online",
      subtitle: "Pay securely using UPI, Card, Wallet or Net Banking",
    },
    // {
    //   id: "cod",
    //   icon: Wallet,
    //   title: "Cash on Delivery",
    //   subtitle: "Pay when you receive the order",
    // },
  ];

  const handlePayment = async (method: "online" | "cod") => {
    if (!deliveryAddressId || !billingAddressId) {
      toast.error("Please select delivery address");
      navigate("/cart");
      return;
    }

    if (!cartData || !cartData.id) {
      toast.error("Cart is empty or invalid");
      navigate("/cart");
      return;
    }

    if (method === "online") {
      try {
        setIsProcessing(true);
        toast.info("Initiating payment...");

        // Call payment API
        const paymentResponse = await initiatePayment({
          shipping_address: deliveryAddressId,
          billing_address: billingAddressId,
          payment_mode: "razorpay",
          cart_id: cartData.id,
        });

        if (!paymentResponse.status || !paymentResponse.proceedPayment) {
          throw new Error("Payment initiation failed");
        }

        // Initialize Razorpay
        const options = {
          key: paymentResponse.data.key,
          amount: paymentResponse.data.amount,
          currency: "INR",
          name: paymentResponse.data.name,
          description: paymentResponse.data.description,
          image: paymentResponse.data.image,
          order_id: paymentResponse.data.order_id,
          prefill: paymentResponse.data.prefill,
          notes: paymentResponse.data.notes,
          theme: paymentResponse.data.theme,
          handler: async function (response: any) {
            // Payment successful - now place the order
            console.log("Payment successful:", response);
            try {
              const orderResponse = await placeOrder({
                cart_id: cartData.id,
                shipping_address: deliveryAddressId,
                billing_address: billingAddressId,
                payment_status: true,
                payment_mode: "razorpay",
                payment_details: {
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_signature: response.razorpay_signature,
                },
              });

              if (orderResponse.status) {
                setShowSuccess(true);
                setTimeout(() => {
                  clearCart();
                  navigate("/");
                  toast.success("Payment successful! Order placed.");
                }, 2000);
              } else {
                throw new Error(
                  orderResponse.message || "Order placement failed"
                );
              }
            } catch (error) {
              console.error("Order placement error:", error);
              toast.error(
                "Payment received but order placement failed. Please contact support."
              );
            }
          },
          modal: {
            ondismiss: async function () {
              // Payment cancelled or failed
              console.log("Payment dismissed/cancelled");
              try {
                // Record failed payment attempt
                await placeOrder({
                  cart_id: cartData.id,
                  shipping_address: deliveryAddressId,
                  billing_address: billingAddressId,
                  payment_status: false,
                  payment_mode: "razorpay",
                  payment_details: null,
                });
              } catch (error) {
                console.error("Failed to record payment cancellation:", error);
              }
              setIsProcessing(false);
              toast.error("Payment cancelled or failed");
            },
          },
        };

        const razorpay = new window.Razorpay(options);
        razorpay.open();
        setIsProcessing(false);
      } catch (err) {
        console.error("Payment error:", err);
        toast.error("Failed to initiate payment. Please try again.");
        setIsProcessing(false);
      }
    } else {
      // Place order with COD
      try {
        setIsProcessing(true);
        toast.info("Placing order...");

        const orderResponse = await placeOrder({
          cart_id: cartData.id,
          shipping_address: deliveryAddressId,
          billing_address: billingAddressId,
          payment_status: true,
          payment_mode: "cashondelivery",
          payment_details: [],
        });

        if (orderResponse.status) {
          setShowSuccess(true);
          setTimeout(() => {
            clearCart();
            navigate("/");
            toast.success("Order placed successfully with Cash on Delivery!");
          }, 2000);
        } else {
          throw new Error(orderResponse.message || "Order placement failed");
        }
      } catch (err) {
        console.error("Order placement error:", err);
        toast.error("Failed to place order. Please try again.");
      } finally {
        setIsProcessing(false);
      }
    }
  };

  if (showSuccess) {
    const firstCartItem = cart[0];
    const finalPrice = firstCartItem ? getFinalPrice(firstCartItem.product) : 0;

    // Get image with proper fallback
    const getImageSrc = () => {
      if (!firstCartItem) return "";
      const product = firstCartItem.product;

      if (product.base_image && product.base_image !== null) {
        const baseUrl = product.s3_base_url || product.media_base_url || "";
        return `${baseUrl}${product.base_image}`;
      }
      if (product.thumb_image && product.thumb_image !== null) {
        const baseUrl = product.s3_base_url || product.media_base_url || "";
        return `${baseUrl}${product.thumb_image}`;
      }
      if (product.default_image) {
        if (
          product.default_image.startsWith("vendor/") ||
          product.default_image.startsWith("product/")
        ) {
          return `https://sparts.in:8443/${product.default_image}`;
        }
        return product.default_image;
      }
      return 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23f0f0f0" width="200" height="200"/%3E%3Ctext fill="%23999" font-family="sans-serif" font-size="16" dy="10.5" font-weight="bold" x="50%25" y="50%25" text-anchor="middle"%3ENo Image%3C/text%3E%3C/svg%3E';
    };

    const imageSrc = getImageSrc();

    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-8 text-center">
          <div className="flex justify-center mb-4">
            <div className="bg-green-100 rounded-full p-3">
              <CheckCircle className="h-12 w-12 text-green-600" />
            </div>
          </div>
          <h2 className="text-2xl font-bold mb-2">
            Your order is booked successfully!
          </h2>
          {firstCartItem && (
            <div className="my-6 flex justify-center">
              <div className="w-24 h-24 bg-muted rounded-lg">
                <img
                  src={imageSrc}
                  alt={firstCartItem.product.name}
                  className="w-full h-full object-contain"
                  onError={(e) => {
                    e.currentTarget.src =
                      'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23f0f0f0" width="200" height="200"/%3E%3Ctext fill="%23999" font-family="sans-serif" font-size="16" dy="10.5" font-weight="bold" x="50%25" y="50%25" text-anchor="middle"%3ENo Image%3C/text%3E%3C/svg%3E';
                  }}
                />
              </div>
            </div>
          )}
          <div className="mb-6">
            {firstCartItem && (
              <>
                <p className="font-semibold">{firstCartItem.product.name}</p>
                <p className="text-sm text-muted-foreground">
                  {firstCartItem.quantity} pc x 1
                </p>
                <p className="text-lg font-bold mt-2">
                  ₹{finalPrice.toLocaleString()}
                </p>
              </>
            )}
            <div className="mt-4 pt-4 border-t">
              <p className="text-sm font-semibold">Delivery Date & Time</p>
            </div>
          </div>
          <Button onClick={() => navigate("/")} className="w-full">
            Go to My Orders
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30">
      <div className="sticky top-0 bg-background border-b z-10 px-4 py-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/cart")}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-xl font-semibold">Payment Options</h1>
        </div>
      </div>

      <div className="container max-w-2xl mx-auto px-4 py-6 space-y-4">
        {paymentMethods.map((method) => {
          const IconComponent = method.icon;
          const isSelected = selectedPaymentMethod === method.id;

          return (
            <Card
              key={method.id}
              className={`p-4 cursor-pointer transition-all ${
                isSelected
                  ? "border-primary border-2 bg-primary/5"
                  : "hover:bg-muted/50"
              }`}
              onClick={() =>
                setSelectedPaymentMethod(method.id as "online" | "cod")
              }
            >
              <div className="flex items-center gap-4">
                <div className="p-3 bg-primary/10 rounded-full">
                  <IconComponent className="h-6 w-6 text-primary" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold">{method.title}</h3>
                  <p className="text-sm text-muted-foreground">
                    {method.subtitle}
                  </p>
                </div>
                {isSelected && <CheckCircle className="h-5 w-5 text-primary" />}
              </div>
            </Card>
          );
        })}

        <div className="pt-4 space-y-3">
          <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
            <span className="font-semibold">Total Amount</span>
            <span className="text-xl font-bold">₹{parseFloat(total).toFixed(2).toLocaleString()}</span>
          </div>

          {selectedPaymentMethod === "online" && (
            <Button
              className="w-full"
              size="lg"
              onClick={() => handlePayment("online")}
              disabled={isProcessing}
            >
              {isProcessing
                ? "Processing..."
                : `Proceed to Pay ₹${total.toLocaleString()}`}
            </Button>
          )}

          {/* {selectedPaymentMethod === "cod" && (
            <Button
              className="w-full"
              size="lg"
              onClick={() => handlePayment("cod")}
              disabled={isProcessing}
            >
              {isProcessing ? "Processing..." : "Place Order"}
            </Button>
          )} */}

          {!selectedPaymentMethod && (
            <Button className="w-full" size="lg" disabled>
              Select Payment Method
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentOptions;
